/**
 * PlanGate — Hard feature enforcement component.
 *
 * Wraps a page or section and blocks access if the user's current plan
 * doesn't include the required feature. Instead of silently hiding content
 * or showing a warning toast, it renders a full inline "locked" screen with
 * an upgrade CTA.
 *
 * Usage:
 *   <PlanGate feature="advancedAnalytics" featureName="Advanced Analytics">
 *     <ReportsPage />
 *   </PlanGate>
 *
 * Available features (from PlanContext.PLANS[tier].features):
 *   dashboard, members, membershipPlans, payments, trainers, attendance,
 *   announcements, basicReports, exportReports, automationReminders,
 *   advancedAnalytics, progressTracking, revenueInsights, whatsappReminders
 */
import React, { useState } from 'react';
import { Lock, Zap, ChevronRight, ArrowUpRight } from 'lucide-react';
import { usePlan, PLANS } from '../context/PlanContext';
import { UpgradeModal } from './UpgradeModal';

// ─── Feature descriptions ────────────────────────────────────────────────────
const FEATURE_META = {
  advancedAnalytics: {
    emoji: '📊',
    headline: 'Advanced Analytics',
    detail:
      'Deep-dive charts, revenue forecasting, member retention trends, and AI-powered insights are locked to Pro.',
  },
  progressTracking: {
    emoji: '📈',
    headline: 'Progress Tracking',
    detail:
      'Member body measurements, progress photos, fitness assessments, and transformation charts require Pro.',
  },
  revenueInsights: {
    emoji: '💰',
    headline: 'Revenue Insights',
    detail:
      'Month-over-month revenue trends, forecast panels, and pending billing analytics need a Pro plan.',
  },
  exportReports: {
    emoji: '📥',
    headline: 'Export Reports',
    detail:
      'Download CSV / Excel reports of members, payments, and attendance. Available on Pro only.',
  },
  automationReminders: {
    emoji: '🔔',
    headline: 'Automation & Reminders',
    detail:
      'Automated WhatsApp & SMS payment reminders, renewal alerts, and scheduled broadcasts need Pro.',
  },
};

// ─── Pro plan feature list for display ──────────────────────────────────────
const PRO_FEATURES = [
  'Unlimited members & trainers',
  'Export CSV/Excel reports',
  'Advanced analytics & forecasting',
  'Progress tracking & body measurements',
  'Automated WhatsApp reminders',
  'Revenue insights & billing dashboard',
];

// ─── Main component ──────────────────────────────────────────────────────────
export function PlanGate({ feature, featureName, children }) {
  const { can, planId, plan } = usePlan();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // If user has access, render children normally
  if (can(feature)) {
    return <>{children}</>;
  }

  const meta = FEATURE_META[feature] || {
    emoji: '🔒',
    headline: featureName || 'This Feature',
    detail: 'This feature is available on the Pro plan. Upgrade to unlock full access.',
  };

  const proPlan = PLANS.pro;

  return (
    <>
      {/* Full-page locked screen — rendered inside the existing page layout */}
      <div
        style={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 24px',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 580,
            textAlign: 'center',
          }}
        >
          {/* Lock icon + emoji */}
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: 22,
              background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(168,85,247,0.12))',
              border: '1.5px solid rgba(99,102,241,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              position: 'relative',
            }}
          >
            <span style={{ fontSize: 32 }}>{meta.emoji}</span>
            <div
              style={{
                position: 'absolute',
                bottom: -8,
                right: -8,
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #6366f1, #a855f7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(99,102,241,0.4)',
              }}
            >
              <Lock size={13} color="#fff" />
            </div>
          </div>

          {/* Plan badge */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(99,102,241,0.08)',
              border: '1px solid rgba(99,102,241,0.2)',
              borderRadius: 99,
              padding: '4px 14px',
              marginBottom: 16,
            }}
          >
            <Zap size={12} style={{ color: '#6366f1' }} />
            <span
              style={{ fontSize: 12, fontWeight: 700, color: '#6366f1', letterSpacing: '0.3px' }}
            >
              Pro Feature
            </span>
          </div>

          {/* Headline */}
          <h2
            style={{
              fontSize: 28,
              fontWeight: 900,
              letterSpacing: '-0.5px',
              margin: '0 0 12px',
              color: 'var(--text-primary, #111)',
              lineHeight: 1.2,
            }}
          >
            {meta.headline} Requires{' '}
            <span
              style={{
                backgroundImage: 'linear-gradient(90deg, #6366f1, #a855f7)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Pro
            </span>
          </h2>

          {/* Detail */}
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.65,
              color: 'var(--text-secondary, #666)',
              margin: '0 auto 32px',
              maxWidth: 440,
            }}
          >
            {meta.detail}
          </p>

          {/* Pro features list */}
          <div
            style={{
              background: 'rgba(99,102,241,0.04)',
              border: '1.5px solid rgba(99,102,241,0.12)',
              borderRadius: 18,
              padding: '20px 24px',
              marginBottom: 28,
              textAlign: 'left',
            }}
          >
            <p
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: '#6366f1',
                margin: '0 0 14px',
              }}
            >
              What you get with Pro
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {PRO_FEATURES.map(f => (
                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: 'rgba(99,102,241,0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <ChevronRight size={11} style={{ color: '#6366f1' }} />
                  </div>
                  <span
                    style={{ fontSize: 14, color: 'var(--text-secondary, #555)', fontWeight: 500 }}
                  >
                    {f}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing callout */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: 4,
              marginBottom: 24,
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--text-muted, #888)', fontWeight: 500 }}>
              Only
            </span>
            <span
              style={{
                fontSize: 28,
                fontWeight: 900,
                color: 'var(--text-primary, #111)',
                letterSpacing: '-0.5px',
              }}
            >
              ₹{proPlan.price.toLocaleString()}
            </span>
            <span style={{ fontSize: 13, color: 'var(--text-muted, #888)', fontWeight: 500 }}>
              /month
            </span>
          </div>

          {/* CTAs */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setUpgradeOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '13px 28px',
                borderRadius: 14,
                border: 'none',
                background: 'linear-gradient(135deg, #6366f1, #a855f7)',
                color: '#fff',
                fontSize: 15,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(99,102,241,0.35)',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(99,102,241,0.45)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(99,102,241,0.35)';
              }}
            >
              <Zap size={16} />
              Upgrade to Pro
              <ArrowUpRight size={15} style={{ opacity: 0.8 }} />
            </button>

            <button
              type="button"
              onClick={() => window.open('mailto:support@fitxero.com', '_blank')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '13px 24px',
                borderRadius: 14,
                border: '1.5px solid rgba(128,128,128,0.18)',
                background: 'transparent',
                color: 'var(--text-secondary, #666)',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(99,102,241,0.35)';
                e.currentTarget.style.color = '#6366f1';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'rgba(128,128,128,0.18)';
                e.currentTarget.style.color = 'var(--text-secondary, #666)';
              }}
            >
              Contact Support
            </button>
          </div>

          {/* Current plan info */}
          <p
            style={{
              marginTop: 20,
              fontSize: 12,
              color: 'var(--text-muted, #aaa)',
            }}
          >
            You are on the{' '}
            <strong style={{ color: 'var(--text-secondary, #777)' }}>{plan.name}</strong> plan.
            {planId === 'trial' && ' Your free trial gives full access — upgrade before it ends!'}
          </p>
        </div>
      </div>

      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        feature={meta.headline}
      />
    </>
  );
}

// ─── Convenience HOC wrapper ─────────────────────────────────────────────────
/**
 * withPlanGate — Higher-order component to wrap a page component.
 * Usage: export default withPlanGate(ReportsPage, 'advancedAnalytics', 'Advanced Analytics');
 */
export function withPlanGate(WrappedComponent, feature, featureName) {
  return function GatedComponent(props) {
    return (
      <PlanGate feature={feature} featureName={featureName}>
        <WrappedComponent {...props} />
      </PlanGate>
    );
  };
}
